import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import { Box, ListItemButton } from '@mui/material';

import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { getPersonFullName } from '@occc-app/lib/person';
import config from '@occc/config';

import ChainsDropdown from './ChainsDropdown';
import { PreparedUser } from './types';
import { userRoleMap } from './constants';

import { FEEDBACK_URL } from '../../../constants';
import { Typography } from '../../Typography';
import { Divider } from '../../Divider';
import { LetterAvatar } from '../../Avatar';
import { List, ListItem, ListItemText, ListItemIcon, ListItemAvatar } from '../../List';
import { ExitToAppIcon, GroupIcon, FeedbackIcon } from '../../../icons';

type MyAccountPopoverProps = {
  logout: () => void;
  handleMenuClose: () => void;
  isMenuOpen: boolean;
  menuAnchorEl: HTMLElement | null;
  user: PreparedUser | null;
};

type AppVersionProps = {
  version: string;
};

const AppVersion = ({ version }: AppVersionProps) => (
  <>
    <Divider />
    <Box py={1} px={2}>
      <Typography variant='caption' color='textSecondary'>
        {version}
      </Typography>
    </Box>
  </>
);

export const MyAccountPopover = ({
  menuAnchorEl,
  handleMenuClose,
  isMenuOpen,
  user,
  logout,
}: MyAccountPopoverProps) => {
  const isUserPermittedToViewUserManagement = useIsUserPermittedTo(Feature.USER_MANAGEMENT_VIEW);

  const onUserManagementClick = () => {
    handleMenuClose();
  };

  const userDisplayName = useMemo(() => {
    const userFullName = getPersonFullName(user?.name);
    return userFullName || user?.email || '';
  }, [user?.name, user?.email]);

  return (
    <Popover
      anchorEl={menuAnchorEl}
      open={isMenuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <List component='div' sx={{ width: 264 }}>
        <ListItem component='div' divider>
          <ListItemAvatar>
            <LetterAvatar>{user?.avatarLetters}</LetterAvatar>
          </ListItemAvatar>
          <ListItemText
            primary={<Typography noWrap>{userDisplayName}</Typography>}
            secondary={user?.userRole ? userRoleMap[user.userRole] : ''}
          />
        </ListItem>
        {!config.isLiteVersion && (
          <>
            <ChainsDropdown />
            <Divider />
          </>
        )}

        {isUserPermittedToViewUserManagement && (
          <ListItemButton
            component={Link}
            to='/users'
            onClick={onUserManagementClick}
            data-testid='userManagement'
          >
            <ListItemIcon>
              <GroupIcon />
            </ListItemIcon>
            <ListItemText primary='User Management' />
          </ListItemButton>
        )}
        <ListItemButton component='a' title='Feedback' href={FEEDBACK_URL} target='_blank'>
          <ListItemIcon>
            <FeedbackIcon />
          </ListItemIcon>
          <ListItemText primary='Feedback' />
        </ListItemButton>
        <ListItemButton onClick={logout} data-testid='logout'>
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary='Log Out' />
        </ListItemButton>
      </List>
      {config.release && <AppVersion version={config.release} />}
    </Popover>
  );
};
